<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <ehc-header
      text="Users"
      :actions="headerActions"
      @create="message = { ...createNewUser }"
      @promote="showPromote = !showPromote"
      @downloadUsers="handleDownloadUsers()"
      @refreshUsersAction="refreshUsers"
      @runArchivedUsersDryRun="runMarkUsersArchived(true)"
      @runArchivedUsersLive="runMarkUsersArchived(false)"
      @toggleShowArchivedUsers="toggleShowArchivedUsers"
    ></ehc-header>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mt-n2 mb-3"
    ></v-progress-linear>
    <v-alert
      v-if="archivedRunResult"
      :type="archivedRunResult.success ? 'success' : 'error'"
      outlined
      class="mb-4"
    >
      <div class="font-weight-bold mb-2">
        {{ archivedRunResult.title }}
      </div>
      <div v-if="archivedRunResult.message" class="mb-2">
        {{ archivedRunResult.message }}
      </div>
      <div
        v-if="archivedRunResult.summary"
        class="text-caption"
        style="white-space: pre-wrap"
      >
        {{ formatArchivedSummary(archivedRunResult.summary) }}
      </div>
    </v-alert>
    <ehc-sheet>
      <!-- Filter & Search Controls -->
      <v-card flat class="mb-4 pa-4" outlined>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="searchInput"
              placeholder="Search by email, name, phone, or ID"
              outlined
              dense
              clearable
              prepend-icon="mdi-magnify"
              @input="debouncedSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="12">
            <v-btn-toggle v-model="dateFilter" mandatory>
              <v-btn value="all" small>All</v-btn>
              <v-btn value="1day" small>Last 1 Day</v-btn>
              <v-btn value="2days" small>Last 2 Days</v-btn>
              <v-btn value="7days" small>Last 7 Days</v-btn>
              <v-btn value="30days" small>Last 30 Days</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex gap-2">
              <v-btn
                small
                outlined
                color="primary"
                @click="resetFilters"
                :disabled="!hasActiveFilters"
              >
                <v-icon small left>mdi-close</v-icon>
                Reset Filters
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Summary Stats -->
        <v-row class="mt-2">
          <v-col cols="12">
            <div class="d-flex align-center gap-4">
              <div>
                <span class="text-overline">Total Users:</span>
                <span class="font-weight-bold text-h6">{{ users.length }}</span>
              </div>
              <div>
                <span class="text-overline">Filtered:</span>
                <span class="font-weight-bold text-h6">{{
                  filteredUsers.length
                }}</span>
              </div>
              <v-spacer></v-spacer>
              <div class="text-caption text-grey">
                Last loaded: {{ lastLoadedTime }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Data Table -->
      <ehc-table
        :key="tableResetKey"
        :headers="tableHeaders"
        :items="filteredUsers"
        :selectable="true"
        pagination="auto"
        :itemsPerPage="30"
        @click:row="navigateToUser"
        @viewOrg="viewOrg($event)"
        @editPermissions="
          message = {
            ...editPermissionsMessage,
            user: $event,
            title: 'change permissions',
            message: 'permissions for ' + $event.email,
          }
        "
        @addPropertyToUser="
          message = { ...addPropertyToUserMessage, user: $event }
        "
        @deactivateUser="openDeactivateDialog($event)"
        :loading="loading"
      >
        <!-- Custom column renderers -->
        <template v-slot:item.createdAt="{ item }">
          <span>{{ item.createdAt }}</span>
        </template>
      </ehc-table>
    </ehc-sheet>

    <!-- Dialogs -->
    <ehc-message
      v-model="message"
      @cancel="onMessageCancel"
      @savePermissions="editPermissions(message.user)"
      @createUser="submitNewUser()"
      @deactivateUserConfirm="deactivateUser()"
    >
      <template #downloadUsers>
        <div class="d-flex justify-center">
          <vue-json-to-csv
            :json-data="message.userData"
            csv-title="EH Users"
            @success="(val) => handleDownloadUsersSuccess(val)"
          >
            <v-btn class="mx-2" small color="primary"> download </v-btn>
          </vue-json-to-csv>
        </div>
      </template>

      <template #editPermissions>
        <v-switch
          v-for="(option, index) in permissionOptions"
          :key="index"
          v-model="message.user.permissions"
          :label="option"
          :value="option"
          small
          dense
        >
        </v-switch>
      </template>

      <template #createNewUser>
        <ehc-form
          dense
          :meta="newUserMeta"
          :shakeInvalid="message.shakeInvalid"
          v-model="message.userData"
        ></ehc-form>
      </template>

      <template #addPropertyToUser>
        <span style="font-size: 0.8em"
          >This feature is in 'beta'. Be sure you enter a valid propertyId<br
        /></span>
        Add an existing property to {{ message.user.email }}:&nbsp;

        <v-text-field
          v-if="!newPropertyMessage"
          v-model="newPropertyId"
          label="New Property Id"
          :append-outer-icon="newPropertyId ? 'mdi-send' : ''"
          @click:append-outer="transferProperty(message.user)"
        >
        </v-text-field>
        <v-checkbox
          v-if="!newPropertyMessage"
          v-model="moveGuestInfo"
          label="Transfer guest data also"
          :value="moveGuestInfo"
          class="my-1"
        ></v-checkbox>
        <p v-if="newPropertyMessage">{{ newPropertyMessage }}</p>
      </template>

      <template #deactivateUser>
        <div>
          <v-alert type="error" outlined class="mb-3">
            <strong>Warning:</strong> This will deactivate the user account,
            disable login, and remove them from all organizations.
          </v-alert>
          <p>
            Are you sure you want to deactivate user
            <strong>{{ message.user.email }}</strong
            >?
          </p>
          <p class="text-caption">Type the email address to confirm:</p>
          <v-text-field
            v-model="confirmDeactivateEmail"
            outlined
            dense
            placeholder="Confirm by typing email"
          ></v-text-field>
        </div>
      </template>
    </ehc-message>
  </ehc-page>
</template>

<script>
import firebase from "firebase";
import mixins from "@/mixins";
import auth from "@/mixins/auth";
import VueJsonToCsv from "vue-json-to-csv";

export default {
  name: "users",
  mixins: [mixins, auth],
  components: {
    "vue-json-to-csv": VueJsonToCsv,
  },
  data: function () {
    return {
      message: {
        show: false,
      },
      addPropertyToUserMessage: {
        show: true,
        width: "700px",
        title: "BETA: add existing property to user",
        slotName: "addPropertyToUser",
      },
      createNewUser: {
        show: true,
        slotName: "createNewUser",
        title: "Create New User",
        userData: {},
        shakeInvalid: false,
        actions: [
          { label: "Cancel", clickEmit: "cancel", dark: false, plain: true },
          { type: "spacer" },
          { label: "Create User", clickEmit: "createUser" },
        ],
      },
      downloadUsers: {
        show: true,
        slotName: "null",
        title: "Download Users",
        userData: [],
        shakeInvalid: false,
        loading: true,
        actions: [
          { label: "Cancel", clickEmit: "cancel", dark: false, plain: true },
          { type: "spacer" },
        ],
      },
      editPermissionsMessage: {
        width: 400,
        slotName: "editPermissions",
        message: null,
        show: true,
        actions: [
          { label: "Cancel", clickEmit: "cancel", dark: false, plain: true },
          { type: "spacer" },
          { label: "Save Permissions", clickEmit: "savePermissions" },
        ],
      },
      deactivateUserMessage: {
        width: "500px",
        slotName: "deactivateUser",
        show: true,
        actions: [
          { label: "Cancel", clickEmit: "cancel", dark: false, plain: true },
          { type: "spacer" },
          {
            label: "Deactivate User",
            clickEmit: "deactivateUserConfirm",
            color: "error",
          },
        ],
      },
      confirmDeactivateEmail: "",
      closeMessage: {
        show: false,
      },
      loading: false,
      search: null,
      showPromote: false,
      newPropertyId: null,
      newPropertyMessage: null,
      moveGuestInfo: true,
      item: {},
      formData: {},
      newUserFormData: {},
      meta: [
        { type: "text", label: "UserId", key: "uid" },
        {
          type: "button",
          label: "submit",
          key: "submit",
          emitOnClick: "submit",
        },
      ],
      newUserMeta: [
        { type: "email", label: "email", key: "email", required: true },
        {
          type: "password",
          label: "password",
          key: "password",
          required: true,
        },
      ],
      searchInput: "",
      searchQuery: "",
      searchTimeout: null,
      dateFilter: "all",
      showArchivedUsers: false,
      users: [],
      usersLastLoaded: null,
      selectedUser: null,
      permissionOptions: [
        "superAdmin",
        "admin",
        "member",
        "viewer",
        "realEstateAdmin",
      ],
      runningArchivedJob: false,
      archivedRunResult: null,
    };
  },
  computed: {
    headerActions() {
      let actions = [
        ...this.userHasPermission(
          ["superAdmin"],
          [
            {
              actionType: "menu",
              icon: "mdi-database-outline",
              items: [
                {
                  type: "link",
                  label: "Refresh",
                  icon: "mdi-refresh",
                  emitClick: "refreshUsersAction",
                  toolTip: "Reload from Firestore",
                },
                { type: "divider" },
                {
                  type: "link",
                  label: this.showArchivedUsers
                    ? "Hide Archived Users"
                    : "Include Archived Users",
                  icon: this.showArchivedUsers
                    ? "mdi-filter-check-outline"
                    : "mdi-filter-remove-outline",
                  emitClick: "toggleShowArchivedUsers",
                  toolTip: this.showArchivedUsers
                    ? "Show only non-archived users"
                    : "Include archived users",
                },
                {
                  type: "link",
                  label: "Archived Users Dry Run",
                  icon: "mdi-play-circle-outline",
                  emitClick: "runArchivedUsersDryRun",
                  toolTip: "Preview user archive changes",
                },
                {
                  type: "link",
                  label: "Run Archive Users Now",
                  icon: "mdi-calendar-check-outline",
                  emitClick: "runArchivedUsersLive",
                  toolTip: "Set/clear archivedAt on eligible users",
                },
              ],
            },
            {
              actionType: "v-btn",
              icon: true,
              iconName: "mdi-download",
              clickEmit: "downloadUsers",
            },
          ],
          []
        ),
        {
          actionType: "v-btn",
          label: "create new user",
          color: "primary",
          dark: true,
          clickEmit: "create",
        },
      ];
      return actions;
    },
    tableHeaders: function () {
      return [
        {
          text: "Email",
          value: "email",
          sortable: true,
          width: "25%",
        },
        {
          type: "menu",
          icon: "mdi-dots-vertical",
          items: [
            {
              type: "link",
              label: "View User Organization",
              emitClick: "viewOrg",
            },
            {
              type: "link",
              label: "Edit Permissions",
              emitClick: "editPermissions",
            },
            {
              type: "link",
              label: "Add Property To User",
              emitClick: "addPropertyToUser",
            },
            {
              type: "link",
              label: "Deactivate User",
              emitClick: "deactivateUser",
              color: "error",
            },
          ],
        },
        {
          text: "Display Name",
          value: "displayName",
          sortable: true,
          width: "20%",
        },
        {
          text: "Created",
          value: "createdAt",
          sortable: true,
          width: "15%",
        },
        {
          text: "Phone",
          value: "phoneNumber",
          sortable: true,
          width: "15%",
        },
        {
          text: "User ID",
          value: "uid",
          sortable: false,
          width: "12%",
        },
      ];
    },
    filteredUsers: function () {
      return this.users
        .filter((user) => {
          // Apply date filter
          if (this.dateFilter === "all") return true;

          if (!user.createdAtRaw) return false;

          const now = new Date();
          const userDate = new Date(user.createdAtRaw);
          const diffTime = now - userDate;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);

          switch (this.dateFilter) {
            case "1day":
              return diffDays <= 1;
            case "2days":
              return diffDays <= 2;
            case "7days":
              return diffDays <= 7;
            case "30days":
              return diffDays <= 30;
            default:
              return true;
          }
        })
        .filter((user) => {
          if (!this.searchQuery) return true;
          const q = this.searchQuery.toLowerCase();
          return (
            user.email?.toLowerCase().includes(q) ||
            user.uid?.toLowerCase().includes(q) ||
            user.displayName?.toLowerCase().includes(q) ||
            user.phoneNumber?.toLowerCase().includes(q)
          );
        });
    },
    tableResetKey: function () {
      return [
        this.searchQuery,
        this.dateFilter,
        this.showArchivedUsers,
        this.users.length,
      ].join("|");
    },
    hasActiveFilters: function () {
      return (
        this.searchInput || this.searchQuery || this.dateFilter !== "all"
      );
    },
    lastLoadedTime: function () {
      if (!this.usersLastLoaded) return "Never";
      const now = new Date();
      const diff = now - this.usersLastLoaded;
      const minutes = Math.floor(diff / 60000);

      if (minutes < 1) return "Just now";
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return this.usersLastLoaded.toLocaleDateString();
    },
  },
  methods: {
    async loadUsersFromFirestore() {
      let query = firebase.firestore().collection("users");
      if (!this.showArchivedUsers) {
        query = query.where("isArchived", "==", false);
      }

      const snapshot = await query.get({ source: "server" });
      let users = [];
      snapshot.forEach((doc) => {
        const user = doc.data();
        if (!this.isActiveUser(user)) {
          return;
        }
        users.push({ uid: doc.id, ...user });
      });

      if (!this.showArchivedUsers && users.length === 0) {
        const allSnap = await firebase
          .firestore()
          .collection("users")
          .get({ source: "server" });
        users = allSnap.docs
          .map((doc) => ({ uid: doc.id, ...doc.data() }))
          .filter(
            (user) =>
              this.isActiveUser(user) && !this.isArchivedUser(user),
          );
      }

      users.sort((a, b) => (a.email || "").localeCompare(b.email || ""));
      return users;
    },
    async handleDownloadUsers() {
      this.message = { ...this.downloadUsers };
      this.message.title = "Loading users...";

      const resp = await this.loadUsersFromFirestore();
      this.message.userData = resp.map((el) => {
        return {
          Email: el.email,
          "Display Name": el.displayName,
          "First Name": el.firstName ? el.firstName : "",
          "Last Name": el.lastName ? el.lastName : "",
          "User Id": el.uid,
          Created: this.formatDate(el.createdAt, "dateOnly"),
        };
      });
      this.message.loading = false;
      this.message.slotName = "downloadUsers";
      this.message.actions = [
        { label: "close", clickEmit: "cancel", dark: false, plain: true },
        { type: "spacer" },
      ];
      this.message.title = "Users loaded";
    },
    handleDownloadUsersSuccess(val) {
      this.message.title = "Downloading...";
      setTimeout(() => {
        this.message.show = false;
      }, 2000);
    },
    submitNewUser: async function () {
      this.message.loading = true;
      const userData = this.message.userData;
      let obj = {
        email: userData.email,
        password: userData.password,
      };
      if (!userData.email || !userData.password) {
        this.message.alert = {
          type: "error",
          text: "Please fill in all fields",
        };
        this.message.shakeInvalid = true;
        this.message.loading = false;
        return;
      }
      if (obj.password.length < 6) {
        this.message.alert = {
          type: "error",
          text: "Password must be at least 6 characters long",
        };
        this.message.shakeInvalid = true;
        this.message.loading = false;
        return;
      }
      let that = this;
      this.message = {
        title: "creating User",
        show: true,
        loading: true,
      };
      await this.apiCreateUserWithEmailAndPassword(obj)
        .then((resp) => {
          console.log(resp);
          this.message = {
            title: "Create New User",
            show: true,
            alert: {
              type: "success",
              text: "user created",
            },
            actions: [{ type: "spacer" }, { label: "Ok", clickEmit: "cancel" }],
          };
        })
        .catch((error) => {
          this.message = {
            ...this.createNewUser,
            alert: {
              type: "error",
              text: error.toString(),
            },
          };
          console.log(this.message);
        });
      this.showNewUser = false;
    },
    formatArchivedSummary(summary) {
      if (!summary) return "";
      const reasons = summary.reasons || {};
      return [
        `dryRun: ${summary.dryRun}`,
        `runAt: ${summary.runAt || "n/a"}`,
        `scanned: ${summary.scanned || 0}`,
        `archived: ${summary.archived || 0}`,
        `unarchived: ${summary.unarchived || 0}`,
        `skippedDeleted: ${summary.skippedDeleted || 0}`,
        `unchanged: ${summary.unchanged || 0}`,
        `errors: ${summary.errors || 0}`,
        `reasons.no_org_memberships: ${reasons.no_org_memberships || 0}`,
        `reasons.all_orgs_archived: ${reasons.all_orgs_archived || 0}`,
        `reasons.has_active_org: ${reasons.has_active_org || 0}`,
      ].join(" | ");
    },
    async runMarkUsersArchived(dryRun = true) {
      if (this.runningArchivedJob) {
        return;
      }
      this.runningArchivedJob = true;
      this.archivedRunResult = null;
      try {
        const callable = firebase.functions().httpsCallable("markUsersArchived");
        const response = await callable({ dryRun });
        const data = response?.data || {};
        this.archivedRunResult = {
          success: true,
          title: dryRun
            ? "Archived users dry run complete"
            : "Archived users run complete",
          message: data.message || "",
          summary: data.summary || null,
        };
        this.$store.commit("setAlert", {
          type: "success",
          text: dryRun
            ? "Dry run completed for user archiving."
            : "User archive job completed.",
        });
        await this.getUsers();
      } catch (error) {
        this.archivedRunResult = {
          success: false,
          title: "Archived users run failed",
          message: error?.message || "Unknown error",
          summary: null,
        };
        this.$store.commit("setAlert", {
          type: "error",
          text: `User archive job failed: ${error?.message || "Unknown error"}`,
        });
      } finally {
        this.runningArchivedJob = false;
      }
    },
    toggleShowArchivedUsers() {
      this.showArchivedUsers = !this.showArchivedUsers;
      this.getUsers();
    },
    async getUsers() {
      this.loading = true;

      try {
        const users = await this.loadUsersFromFirestore();
        this.users = this.enrichUserData(users);
        this.usersLastLoaded = new Date();
      } catch (error) {
        console.error("Error loading users:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text: "Error loading users: " + (error.message || "Unknown error"),
        });
      } finally {
        this.loading = false;
      }
    },
    enrichUserData(users) {
      return users.map((user) => {
        const createdDate = user.createdAt
          ? user.createdAt.substring(0, 10)
          : "N/A";

        return {
          ...user,
          createdAt: createdDate,
          createdAtRaw: user.createdAt,
          phoneNumber: user.phoneNumber || "N/A",
          displayName: user.displayName || "N/A",
        };
      });
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchQuery = this.searchInput || "";
      }, 300);
    },
    resetFilters() {
      clearTimeout(this.searchTimeout);
      this.searchInput = "";
      this.searchQuery = "";
      this.dateFilter = "all";
    },
    navigateToUser(user) {
      this.$router.push(`/user/${user.uid}`);
    },
    refreshUsers() {
      this.getUsers();
    },
    exportUsersToCSV() {
      const headers = ["Email", "Display Name", "Phone", "Created", "User ID"];
      const rows = this.filteredUsers.map((user) => [
        user.email || "",
        user.displayName || "",
        user.phoneNumber || "",
        user.createdAt || "",
        user.uid || "",
      ]);

      // Add headers as first row
      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          row
            .map((cell) => {
              // Escape quotes and wrap in quotes if needed
              if (
                typeof cell === "string" &&
                (cell.includes(",") || cell.includes('"'))
              ) {
                return `"${cell.replace(/"/g, '""')}"`;
              }
              return cell;
            })
            .join(",")
        ),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    viewOrg(user) {
      this.apiGetOrgFromUser(user.uid).then((resp) => {
        this.$router.push({ name: "organization", params: { id: resp.id } });
      });
    },
    onMessageCancel() {
      this.confirmDeactivateEmail = "";
      this.message = { ...this.closeMessage };
    },
    openDeactivateDialog(user) {
      if (user.uid === this.user?.uid) {
        this.$store.commit("setAlert", {
          type: "error",
          text: "You cannot deactivate your own account.",
        });
        return;
      }
      this.confirmDeactivateEmail = "";
      this.message = {
        ...this.deactivateUserMessage,
        user,
        title: "Deactivate User",
      };
    },
    async deactivateUser() {
      const user = this.message.user;
      if (this.confirmDeactivateEmail !== user.email) {
        this.$store.commit("setAlert", {
          type: "error",
          text: "Email does not match. Deactivation cancelled.",
        });
        return;
      }

      this.message = {
        ...this.message,
        loading: true,
        persistent: true,
        message: "Deactivating user...",
      };

      try {
        const softDeleteUser = firebase
          .functions()
          .httpsCallable("softDeleteUser");
        await softDeleteUser({ uid: user.uid });
        this.users = this.users.filter((u) => u.uid !== user.uid);
        this.confirmDeactivateEmail = "";
        this.message = { ...this.closeMessage };
        this.$store.commit("setAlert", {
          type: "success",
          text: "User deactivated successfully",
        });
        await this.getUsers();
      } catch (error) {
        console.error("Error deactivating user:", error);
        this.message = {
          ...this.deactivateUserMessage,
          user,
          title: "Deactivate User",
        };
        this.$store.commit("setAlert", {
          type: "error",
          text:
            "Error deactivating user: " +
            (error.message || "Unknown error occurred"),
        });
      }
    },
    showEditPermissionsDialog(user) {
      this.selectedUser = { ...user };
      if (!this.selectedUser.permissions) {
        this.selectedUser.permissions = [];
      }
      this.permissionDialog.show = true;
    },
    editPermissions(user) {
      if (!("permissions" in user)) {
        user.permissions = [];
      }

      this.message = {
        title: "Applying to " + user.displayName,
        show: true,
        loading: true,
        width: "400px",
        persistent: true,
        message: "Applying Permissions...",
      };

      this.apiUpdateUser(user.uid, { permissions: user.permissions }).then(
        (resp) => {
          this.message = this.closeMessage;
        }
      );
    },
    showAddPropertyDialog(user) {
      this.selectedUser = user;
      this.newPropertyId = null;
      this.newPropertyMessage = null;
      this.addPropertyDialog.show = true;
    },
    async transferProperty(user) {
      if (!this.newPropertyId) {
        this.newPropertyMessage = "Please enter a property ID";
        return;
      }

      try {
        const propertyExists = await this.$store.dispatch(
          "doesPropertyExist",
          this.newPropertyId.trim()
        );

        if (!propertyExists) {
          this.newPropertyMessage = "Not a valid property ID";
          return;
        }

        if (this.moveGuestInfo) {
          await this.$store.dispatch("moveGuestDataToUser", {
            uid: user.uid,
            propertyId: this.newPropertyId.trim(),
          });
        }

        await this.$store.dispatch("updatePropertyUid", {
          uid: user.uid,
          propertyId: this.newPropertyId.trim(),
        });

        this.newPropertyMessage = "Property transferred successfully!";
        setTimeout(() => {
          this.addPropertyDialog.show = false;
          this.newPropertyMessage = null;
          this.newPropertyId = null;
        }, 2000);
      } catch (err) {
        this.newPropertyMessage = err.message || "Error transferring property";
      }
    },
  },
  mounted() {
    this.getUsers();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}
</style>
